import React, { useState, useEffect, useCallback } from 'react';
import { deleteBook, fetchCategories } from '../../../components/API/Store/bookAPI';
import './ListBooks.css';

const robustDecode = (url) => {
    if (!url) return '';
    let decodedUrl = url;
    const iterations = 5;

    for (let i = 0; i < iterations; i++) {
        let previousUrl = decodedUrl;
        decodedUrl = decodedUrl
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#039;/g, "'")
            .replace(/&#x2F;/g, '/')
            .replace(/&#x3D;/g, '=')
            .replace(/\+/g, ' ');

        try {
            decodedUrl = decodeURIComponent(decodedUrl);
        } catch (e) {
            console.warn('Decoding stopped due to error:', e);
            return decodedUrl;
        }

        if (previousUrl === decodedUrl) break;
    }

    return decodedUrl;
};

const ListBooks = ({ books, onEdit, onAdd }) => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadCategories = useCallback(async () => {
        try {
            const response = await fetchCategories();
            if (response.success) {
                setCategories(response.categories);
            } else {
                throw new Error(response.message);
            }
        } catch (err) {
            setError('Failed to load categories. ' + err.message);
        }
    }, []);

    useEffect(() => {
        const initializeComponent = async () => {
            await loadCategories();
            setLoading(false);
        };
        initializeComponent();
    }, [loadCategories]);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this book?')) {
            try {
                const response = await deleteBook(id);
                if (response.success) {
                    onEdit(null); // Trigger a refresh in the parent component
                } else {
                    throw new Error(response.message);
                }
            } catch (err) {
                setError('Failed to delete book. ' + err.message);
            }
        }
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    if (loading || !books) {
        return <div className="loading">Loading books...</div>;
    }

    const filteredBooks = selectedCategory
        ? books.filter((book) => book.category_id === selectedCategory)
        : books;

    return (
        <div className="list-books">
            <div className="header">
                <h1>Book Inventory</h1>
                <div className="actions">
                    <select value={selectedCategory} onChange={handleCategoryChange}>
                        <option value="">All Categories</option>
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    <button className="btn-add" onClick={onAdd}>
                        Add Book
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {books.length === 0 ? (
                <div className="no-books">No books available.</div>
            ) : (
                <div className="books-grid">
                    {filteredBooks.map((book) => (
                        <div className="book-card" key={book.id}>
                            <div className="book-image">
                                {book.cover_image ? (
                                    <img src={robustDecode(book.cover_image)} alt={book.title} />
                                ) : (
                                    <div className="no-image">No Image</div>
                                )}
                            </div>
                            <div className="book-details">
                                <h3>{book.title}</h3>
                                <p className="author">{book.author}</p>
                                <p className="price">${parseFloat(book.price).toFixed(2)}</p>
                                <p className="stock">In stock: {book.stock}</p>
                                <p className="isbn">ISBN: {book.isbn}</p>
                                <p className="published">Published: {new Date(book.publication_date).toLocaleDateString()}</p>
                                <p className="category">
                                    Category: {categories.find((c) => c.id === book.category_id)?.name || 'Uncategorized'}
                                </p>
                                <p className="partner-name">Partner: {book.partner_name || 'Unknown'}</p>
                                <p className="description">{book.description}</p>
                                <div className="book-actions">
                                    <button className="btn-edit" onClick={() => onEdit(book.id)}>
                                        Edit
                                    </button>
                                    <button onClick={() => handleDelete(book.id)} className="btn-delete">
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ListBooks;
